export const ALIASES = [
  "finance@themago.com",
  "business@themago.com",
  "service@themago.com",
] as const;

export type Alias = (typeof ALIASES)[number];

export interface Session {
  username: string;
  aliases: Alias[];
  sendingEnabled: boolean;
}

export interface MessageSummary {
  id: string;
  direction: "inbound" | "sent";
  mailbox: Alias;
  from_address: string;
  from_name: string | null;
  to_json: string;
  subject: string;
  preview: string;
  received_at: number | null;
  sent_at: number | null;
  is_read: number;
  has_attachments: number;
  size: number;
  status: string;
}

export interface Attachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  content_id: string | null;
}

export interface MessageDetail extends Omit<MessageSummary, "preview"> {
  message_id: string | null;
  in_reply_to: string | null;
  references_header: string | null;
  cc_json: string;
  reply_to: string | null;
  text_body: string;
  html_body: string;
  body_truncated: number;
  attachments: Attachment[];
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...options,
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(data.error || `请求失败 (${response.status})`);
  return data as T;
}

export const api = {
  session: () => request<Session>("/api/session"),
  login: (username: string, password: string) => request<Session>("/api/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  }),
  logout: () => request<{ ok: true }>("/api/logout", { method: "POST", body: "{}" }),
  messages: (folder: "inbound" | "sent", alias: string, q: string) => {
    const params = new URLSearchParams({ folder });
    if (alias) params.set("alias", alias);
    if (q) params.set("q", q);
    return request<{ messages: MessageSummary[] }>(`/api/messages?${params}`);
  },
  message: (id: string) => request<{ message: MessageDetail }>(`/api/messages/${id}`),
  mark: (id: string, isRead: boolean) => request<{ ok: true }>(`/api/messages/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ isRead }),
  }),
  remove: (id: string) => request<{ ok: true }>(`/api/messages/${id}`, { method: "DELETE", body: "{}" }),
  send: (payload: { from: Alias; to: string; subject: string; text: string; replyToMessageRowId?: string }) =>
    request<{ ok: true; id: string }>("/api/send", { method: "POST", body: JSON.stringify(payload) }),
};

